import { useState } from "react";
import { ImagePlus } from "lucide-react";
import GifPickerModal from "@/components/GifPickerModal";

/**
 * A slim toolbar rendered above forum textareas. Currently ships a single
 * action (Insert GIF), but the layout leaves room for future buttons
 * (bold / spoiler / mention).
 *
 * Props:
 *  - textareaRef: React ref pointing at the target <textarea>
 *  - value:       current textarea value (controlled)
 *  - onChange:    setter that updates the controlled value with a new string
 *  - testidPrefix: optional prefix for the GIF button data-testid
 */
export default function ForumEditorToolbar({ textareaRef, value, onChange, testidPrefix = "editor" }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const insertAtCursor = (snippet) => {
    const el = textareaRef?.current;
    if (!el) {
      onChange(`${value ? value.replace(/\s*$/, "\n") : ""}${snippet}\n`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end   = el.selectionEnd   ?? value.length;
    // Ensure snippet sits on its own line for clean rendering
    const before = value.slice(0, start);
    const after  = value.slice(end);
    const needsLeadingNL  = before && !before.endsWith("\n") ? "\n" : "";
    const needsTrailingNL = after  && !after.startsWith("\n") ? "\n" : "";
    const next = before + needsLeadingNL + snippet + needsTrailingNL + after;
    onChange(next);
    // Restore focus + move cursor after the inserted block
    setTimeout(() => {
      const pos = (before + needsLeadingNL + snippet + needsTrailingNL).length;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleGifSelect = (gif) => {
    if (!gif?.url) return;
    insertAtCursor(gif.url);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5" data-testid={`${testidPrefix}-toolbar`}>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] md:text-[11px] font-mono tracking-[0.2em] uppercase border border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-cyan)] hover:border-[var(--t-blue)] transition"
          data-testid={`${testidPrefix}-gif-btn`}
          title="Insert a GIF"
        >
          <ImagePlus className="w-3.5 h-3.5" /> GIF
        </button>
        <span className="ml-2 font-mono text-[9px] tracking-[0.25em] uppercase text-[var(--t-text-muted)] hidden sm:inline">
          // paste any image url — it auto-embeds
        </span>
      </div>
      <GifPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleGifSelect}
      />
    </>
  );
}
